import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Switch');

function Switch({
    classes: classesProp,
    className: classNameProp,
    isDisabled,
    isEnriched = false,
    overrides: overridesProp,
    checked = false,
    onChange = () => {},
    size = 'medium',
    ...props
}) {
    const classes = useClasses(useStyles, classesProp);
    const [hovered, setHovered] = useState(false);
    const override = getOverrides(overridesProp, Switch.overrides);

    const handleChange = useCallback(
        (e) => {
            if (isDisabled) return;
            onChange(e.target.checked);
        },
        [isDisabled, onChange],
    );

    const handleMouseEnter = useCallback(() => setHovered(true), []);
    const handleMouseLeave = useCallback(() => setHovered(false), []);

    const rootClassName = classnames(
        classes.root,
        classes[size],
        {
            [classes.disabled]: isDisabled,
        },
        classNameProp,
    );

    const trackClassName = classnames(classes.track, {
        [classes.trackEnriched]: isEnriched,
        [classes.trackChecked]: !isEnriched && checked && !hovered,
        [classes.trackHovered]: !isEnriched && hovered && !checked && !isDisabled,
        [classes.trackCheckedHovered]: !isEnriched && hovered && checked && !isDisabled,
        [classes.trackDisabled]: isDisabled,
        [classes.trackDisabledOff]: !isEnriched && isDisabled && !checked,
        [classes.trackDisabledOn]: !isEnriched && isDisabled && checked,
    });

    const handleClassName = classnames(classes.handle, {
        [classes.handleChecked]: checked,
    });

    return (
        <label
            className={rootClassName}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...override.root}
        >
            <input
                type="checkbox"
                className={classes.input}
                checked={checked}
                disabled={isDisabled}
                onChange={handleChange}
                {...props}
                {...override.input}
            />
            <span className={trackClassName} {...override.track}>
                <span className={handleClassName} {...override.handle} />
            </span>
        </label>
    );
}

Switch.overrides = ['root', 'input', 'track', 'handle'];

Switch.propTypes = {
    /** Current state of the switch - true for on/checked, false for off/unchecked */
    checked: PropTypes.bool,
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** When true, the switch cannot be toggled and appears grayed out */
    isDisabled: PropTypes.bool,
    /** When true, fills the switch track with the promotional gradient to indicate AI-enriched content */
    isEnriched: PropTypes.bool,
    /** Size variant of the switch - affects both width and height */
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    /** Function called when the switch is toggled with the new state (true/false) as parameter */
    onChange: PropTypes.func,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
};

export default React.memo(Switch);
